import { lazy, Suspense, useSyncExternalStore } from 'react'
import { ArrowRight, Calculator, CalendarDays, Database, ExternalLink, HeartPulse, Lightbulb, LockKeyhole, MessageCircleMore, Microscope, ShieldCheck, Sparkles, Stethoscope } from 'lucide-react'
import { Link } from 'react-router-dom'
import { ArticleCard } from '../components/ArticleCard'
import { BmiCalculator } from '../components/BmiCalculator'
import { HealthTrendChart, type HealthTrendDataset } from '../components/HealthTrendChart'
import { PageMeta } from '../components/PageMeta'
import { Reveal } from '../components/Reveal'
import { SectionHeading } from '../components/SectionHeading'
import { blogPosts } from '../data/blogs'
import { openHealthStats } from '../data/openHealthStats'
import { healthTrends } from '../data/healthTrends'

const HealthScene = lazy(() => import('../components/HealthScene').then((module) => ({ default: module.HealthScene })))

function InteractiveHealthScene() {
  const mounted = useSyncExternalStore(() => () => undefined, () => true, () => false)
  if (!mounted) return <div className="scene-fallback"><span /> Preparing the interactive scene…</div>
  return <Suspense fallback={<div className="scene-fallback"><span /> Preparing the interactive scene…</div>}><HealthScene /></Suspense>
}

const careCards = [
  {
    icon: Stethoscope,
    number: '01',
    title: 'Start with your story',
    copy: 'Symptoms matter, and so does the context around them. A good visit begins by listening before narrowing the next step.',
  },
  {
    icon: HeartPulse,
    number: '02',
    title: 'Make the plan useful',
    copy: 'Clear explanations, realistic priorities, and practical follow-up help turn a consultation into everyday action.',
  },
  {
    icon: MessageCircleMore,
    number: '03',
    title: 'Keep the conversation open',
    copy: 'Questions are welcome. Digital tools and resources can make it easier to prepare, remember, and participate.',
  },
]

const trendTones = ['teal', 'coral', 'blue'] as const

const healthTrendChartDatasets: HealthTrendDataset[] = healthTrends.map((trend, index) => ({
  id: trend.id,
  label: trend.shortLabel,
  metricLabel: trend.label,
  unit: trend.unit,
  tone: trendTones[index] ?? 'teal',
  points: trend.points.map((point) => ({ label: String(point.year), value: point.value })),
  source: { label: trend.sourceLabel, url: trend.sourceUrl },
  insight: trend.context,
  caveat: trend.caveat,
  formatValue: (value) => trend.id === 'dpt-immunisation'
    ? `${value.toFixed(1)}%`
    : trend.id === 'under-five-mortality'
      ? `${value.toFixed(1)} per 1,000`
      : `${value.toFixed(1)} years`,
}))

export default function HomePage() {
  const latestPosts = [...blogPosts].sort((a, b) => b.published.localeCompare(a.published) || a.title.localeCompare(b.title)).slice(0, 3)

  return (
    <>
      <PageMeta
        title="Thoughtful care for real life"
        description="Meet Dr. Fathima Finu, calculate adult BMI privately, explore real public-health trends, evidence-led guides, patient apps, research updates, and consultation options."
        image="/images/dr-fathima-finu.jpg"
        imageAlt="Professional portrait of Dr. Fathima Finu"
      />

      <section className="home-hero">
        <div className="hero-ambient hero-ambient-one" />
        <div className="hero-ambient hero-ambient-two" />
        <div className="container hero-grid">
          <div className="hero-copy">
            <Reveal>
              <span className="eyebrow"><Sparkles size={14} /> Clear medicine. Human connection.</span>
              <h1>Health makes more sense when we <em>explore it together.</em></h1>
              <p className="hero-lead">I’m Dr. Fathima Finu, a general doctor sharing thoughtful care, practical tools, and reliable health education for the decisions people make every day.</p>
              <div className="hero-actions">
                <Link className="button" to="/booking">Book a consultation <ArrowRight size={18} /></Link>
                <Link className="button button-ghost" to="/insights">Explore health guides</Link>
              </div>
              <div className="hero-trust">
                <span><ShieldCheck size={18} /> Evidence-led</span>
                <span><HeartPulse size={18} /> Whole-person care</span>
                <span><MessageCircleMore size={18} /> Questions welcome</span>
              </div>
            </Reveal>
          </div>
          <Reveal className="hero-visual" delay={0.12}>
            <div className="hero-image-wash" />
            <InteractiveHealthScene />
          </Reveal>
        </div>
        <div className="hero-scroll" aria-hidden="true"><span /> Scroll to discover</div>
      </section>

      <section className="signal-strip">
        <div className="container signal-grid">
          <div><strong>General medicine</strong><span>Everyday health, seen in context</span></div>
          <div><strong>Critical-care perspective</strong><span>Calm thinking when details matter</span></div>
          <div><strong>Malayalam · English · Hindi</strong><span>Care begins with understanding</span></div>
        </div>
      </section>

      <section className="section quick-tools-section" id="health-tools">
        <div className="container">
          <Reveal className="quick-tools-intro">
            <div className="quick-tools-copy">
              <span className="eyebrow"><Calculator size={15} /> Interactive health tools</span>
              <h2>Useful numbers, held with <span className="serif-accent">the right context.</span></h2>
              <p>Start with a simple adult BMI estimate, then take the result as one clue—not a verdict. The calculator runs entirely in your browser and sends nothing to this website.</p>
              <div className="quick-tools-badges">
                <span><LockKeyhole size={15} /> Local-only calculation</span>
                <span><ShieldCheck size={15} /> Screening, not diagnosis</span>
              </div>
            </div>
            <figure className="quick-tools-art">
              <img src="/images/health-tools-concept.png" width="1024" height="1024" alt="Colorful three-dimensional scale, measuring tape, heart, water glass, and checklist illustration" loading="lazy" decoding="async" />
              <figcaption>AI-generated wellness concept art</figcaption>
            </figure>
          </Reveal>

          <Reveal className="quick-tools-calculator" delay={0.08}><BmiCalculator /></Reveal>

          <div className="quick-tools-resources" aria-label="BMI information sources">
            <strong>Read the number responsibly</strong>
            <span>BMI does not directly measure body fat, muscle or individual health.</span>
            <a href="https://www.cdc.gov/bmi/about/index.html" target="_blank" rel="noreferrer">CDC: About BMI <ExternalLink size={13} /></a>
            <a href="https://www.who.int/news-room/fact-sheets/detail/obesity-and-overweight" target="_blank" rel="noreferrer">WHO: Weight and health context <ExternalLink size={13} /></a>
            <Link to="/booking">Discuss your health in context <ArrowRight size={13} /></Link>
          </div>
        </div>
      </section>

      <section className="section health-data-section" id="health-data">
        <div className="container">
          <Reveal className="health-data-intro">
            <div>
              <span className="eyebrow"><Database size={15} /> Health by the numbers</span>
              <h2>Open data, made <span className="serif-accent">human-sized.</span></h2>
              <p>Six global snapshots show where health is improving—and where access, prevention, and fairness still need attention. Every number links to its public source and carries a reading note.</p>
            </div>
            <div className="health-data-review">
              <span>Source-checked snapshot</span>
              <strong>3 October 2026</strong>
              <small>Public aggregates · not personal medical guidance</small>
            </div>
          </Reveal>

          <Reveal className="health-trend-chart-wrap" delay={0.06}>
            <HealthTrendChart
              title="Plot the progress—and the setbacks."
              description="Switch between three real 2000–2024 world aggregates. Hover, tap, or focus a point for its value, and open the table to inspect every plotted result. Source data were retrieved on 3 October 2026."
              datasets={healthTrendChartDatasets}
              initialDatasetId="life-expectancy"
              xAxisLabel="Year"
            />
          </Reveal>

          <div className="health-data-layout">
            <Reveal className="health-data-art">
              <img src="/images/prevention-landscape.webp" alt="Colorful 3D landscape with a glass heart, citrus, leaves, water, and sun and moon motifs" />
              <div>
                <span className="eyebrow">The wider picture</span>
                <h3>A statistic can start a question. It cannot tell your whole story.</h3>
                <p>Country, income, age, sex, access to care, and how data are collected all shape these estimates. Use them to explore—not to self-diagnose.</p>
              </div>
            </Reveal>

            <div className="health-stat-grid">
              {openHealthStats.map((stat, index) => (
                <Reveal className={`health-stat health-stat-${stat.colorTone}`} key={stat.id} delay={(index % 2) * 0.05}>
                  <span className="health-stat-scope">{stat.yearScope}</span>
                  <strong>{stat.displayValue}</strong>
                  <h3>{stat.label}</h3>
                  <p>{stat.context}</p>
                  <details>
                    <summary>How to read this</summary>
                    <p>{stat.caveat}</p>
                  </details>
                  <a href={stat.sourceUrl} target="_blank" rel="noreferrer">{stat.sourceLabel} <ExternalLink size={13} /></a>
                </Reveal>
              ))}
            </div>
          </div>
          <p className="health-data-note">Values are pinned, source-checked public-data snapshots rather than live feeds. Definitions and estimates can be revised by their source organisations.</p>
        </div>
      </section>

      <section className="section care-section">
        <div className="container">
          <Reveal>
            <SectionHeading
              eyebrow="A more useful appointment"
              title={<>Care built around <span className="serif-accent">clarity, not complexity.</span></>}
              copy="Modern medicine can feel overwhelming. My approach is to make the next step understandable, proportionate, and personal."
            />
          </Reveal>
          <div className="care-grid">
            {careCards.map((card, index) => (
              <Reveal className="care-card" key={card.title} delay={index * 0.08}>
                <span className="card-number">{card.number}</span>
                <card.icon size={28} />
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section story-section" id="meet-doctor">
        <div className="container story-grid">
          <Reveal className="story-visual doctor-profile-card">
            <div className="story-portrait-frame">
              <img className="story-portrait" src="/images/dr-fathima-finu.jpg" srcSet="/images/dr-fathima-finu-640.jpg 640w, /images/dr-fathima-finu.jpg 1000w" sizes="(max-width: 860px) min(100vw - 44px, 350px), 350px" width="1000" height="1000" alt="Professional portrait of Dr. Fathima Finu" loading="lazy" decoding="async" />
              <span className="story-photo-tag"><HeartPulse size={15} /> Patient-centred care</span>
            </div>
            <div className="story-card-caption"><span><small>General doctor</small><strong>Dr. Fathima Finu M. P.</strong></span><Stethoscope size={25} aria-hidden="true" /></div>
          </Reveal>
          <Reveal className="story-copy" delay={0.1}>
            <span className="eyebrow">Meet your doctor</span>
            <h2>Clinical experience, a curious mind, and a belief that patients deserve the full picture.</h2>
            <p>My background spans general medicine, critical care, outpatient visits, ward care, and multidisciplinary teamwork. Those settings taught me that better care often starts with one simple thing: making room for the right question.</p>
            <blockquote>“Good healthcare should leave you better informed—not more intimidated.”</blockquote>
            <Link className="text-link" to="/about">More about my approach <ArrowRight size={17} /></Link>
          </Reveal>
        </div>
      </section>

      <section className="section insights-preview">
        <div className="container">
          <Reveal className="heading-row">
            <SectionHeading eyebrow="The health edit" title={<>Small reads. <span className="serif-accent">Useful changes.</span></>} copy="Fresh, evidence-based explainers for prevention, medicines, and healthier everyday routines." />
            <Link className="button button-ghost" to="/insights">View all {blogPosts.length} guides <ArrowRight size={17} /></Link>
          </Reveal>
          <div className="article-grid home-article-grid">
            {latestPosts.map((post, index) => (
              <Reveal key={post.slug} delay={index * 0.08}><ArticleCard post={post} featured={index === 0} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section platform-section">
        <div className="container platform-grid">
          <Reveal className="platform-copy">
            <span className="eyebrow">Beyond the clinic</span>
            <h2>Tools for the days <span className="serif-accent">between visits.</span></h2>
            <p>Two Android concepts are being developed to make daily nutrition and patient organisation simpler. No guilt, no noise—just practical support and clearer patterns.</p>
            <Link className="button button-light" to="/apps">Preview the apps <ArrowRight size={18} /></Link>
          </Reveal>
          <Reveal className="phone-stage" delay={0.1}>
            <div className="phone phone-back">
              <div className="phone-screen nutrition-screen">
                <span className="mini-label">TODAY</span>
                <h3>A gentler rhythm</h3>
                <div className="meal-ring"><strong>3</strong><span>check-ins</span></div>
                <div className="mini-progress"><span style={{ width: '72%' }} /></div>
                <p>Hydration is building nicely.</p>
              </div>
            </div>
            <div className="phone phone-front">
              <div className="phone-screen patient-screen">
                <span className="mini-label">NEXT VISIT</span>
                <h3>Your questions, ready</h3>
                <div className="question-row"><span>01</span> What changed?</div>
                <div className="question-row"><span>02</span> Medicines list</div>
                <div className="question-row"><span>03</span> Follow-up plan</div>
                <button type="button">Build my visit brief</button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section discovery-section">
        <div className="container">
          <Reveal>
            <SectionHeading align="center" eyebrow="Knowledge in motion" title={<>Discover. Question. <span className="serif-accent">Contribute.</span></>} copy="A health hub is better when it explains what is changing and makes space for lived experience—without trading away privacy." />
          </Reveal>
          <div className="discovery-grid">
            <Reveal className="discovery-card research-discovery">
              <Microscope size={28} />
              <span className="eyebrow">Research watch</span>
              <h3>See what’s promising—and what’s still being tested.</h3>
              <p>Plain-language updates on emerging medicines, devices, and clinical research, with status labels and primary sources.</p>
              <Link className="text-link" to="/research">Follow the evidence <ArrowRight size={17} /></Link>
            </Reveal>
            <Reveal className="discovery-card community-discovery" delay={0.08}>
              <Lightbulb size={28} />
              <span className="eyebrow">Community ideas</span>
              <h3>Share a pattern, barrier, or idea that research should understand.</h3>
              <p>Submit a focused experience or research question through a private intake, with one optional supporting document. Nothing is posted publicly or treated as automatic study enrolment.</p>
              <Link className="text-link" to="/community">Share thoughtfully <ArrowRight size={17} /></Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section booking-banner-section">
        <Reveal className="container booking-banner">
          <div>
            <span className="eyebrow"><CalendarDays size={15} /> Consultation</span>
            <h2>Ready to talk about what’s been on your mind?</h2>
            <p>Request an online or in-person consultation. Clinic location and final timings will be confirmed before launch and with every accepted appointment.</p>
          </div>
          <Link className="button button-light" to="/booking">Request a time <ArrowRight size={18} /></Link>
        </Reveal>
      </section>
    </>
  )
}
